import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { catchError, of } from 'rxjs';
import { format } from 'date-fns';
import {
  resolveFirstAvailableDate,
  schedulingDateIssue,
  schedulingDueDay,
} from './first-available-date';

import { formatPersonNameLastFirst } from '@app/shared/util/person-name';
import { createTerminologyUsageSignals } from '@app/shared/terminology/terminology-usage-signals';
import { ToastService } from '@app/shared/services/toast.service';
import {
  ReadyToScheduleRow,
  SCHEDULE_BLOCKED_TOAST_TITLE,
  ScheduleHearingRequest,
  ScheduleHearingResult,
  scheduleWriteErrorMessage,
} from './hearing-schedule.models';
import { HearingScheduleService } from './hearing-schedule.service';
import {
  combineDateTime,
  isBeforeToday,
  toDateInput,
  toTimeInput,
} from './reschedule-hearing-dialog.component';
import { PHMS_HEARING_TERMINOLOGY } from '../phms-hearing.terminology';

export interface ScheduleHearingDialogData {
  /** The ready-to-schedule candidate being placed on the calendar. */
  candidate: ReadyToScheduleRow;
  /**
   * Prefill for the date + start-time inputs: the dropped/picked day at the business-day start
   * hour (09:00). The scheduler edits any of it before confirming.
   */
  defaultStart: Date;
}

/** Default hearing length: end prefills (and auto-follows) two hours after the start. */
export const SCHEDULE_DEFAULT_DURATION_MINUTES = 120;

/**
 * "Schedule Hearing" confirmation dialog: every unscheduled-inmate scheduling entry point
 * (drag-to-a-day from the eligible strip, the dashboard's Schedule deep-link, and the search
 * tray's Add-to-Calendar day picker) lands here INSTEAD of insta-booking the hearing at 9:00 AM.
 * Sibling of the Reschedule dialog (story 1445) and deliberately built in its image: prefilled
 * date/start/end/location inputs, Confirm posts through the SAME schedule write endpoint the old
 * instant path used (server-side conflict validation stays authoritative), and conflict/
 * permission/validation rejections raise the shared "Cannot schedule" EZ notification toast
 * (identical to the drag/drop calendar path and the Reschedule dialog), leaving the dialog open so
 * the scheduler can adjust and retry. Cancel (or Escape) closes without any request. CDK Dialog
 * provides the focus trap.
 */
@Component({
  selector: 'phms-schedule-hearing-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './schedule-hearing-dialog.component.html',
  styleUrl: './schedule-hearing-dialog.component.scss',
})
export class ScheduleHearingDialogComponent {
  readonly hearingText = createTerminologyUsageSignals(
    PHMS_HEARING_TERMINOLOGY.scheduleDialog,
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly scheduleService = inject(HearingScheduleService);
  private readonly toast = inject(ToastService);
  private readonly dialogRef = inject(
    DialogRef<ScheduleHearingResult | undefined>,
  );
  readonly data = inject<ScheduleHearingDialogData>(DIALOG_DATA);

  /** Header renders the person as "Last, First Middle" (#1436); the raw title stays on the row. */
  readonly formatPersonName = formatPersonNameLastFirst;

  readonly isSaving = signal(false);

  // Editable fields, prefilled from the picked day (date + 09:00 start, end two hours later) and
  // the candidate's projected location/room — the same values the old instant path submitted.
  date = toDateInput(this.data.defaultStart.toISOString());
  startTime = toTimeInput(this.data.defaultStart.toISOString());
  endTime = addMinutesToTime(this.startTime, SCHEDULE_DEFAULT_DURATION_MINUTES);
  locationName = this.data.candidate.locationName ?? '';
  roomName = this.data.candidate.roomName ?? '';

  /** True once the user touches the end time, which stops it auto-following the start. */
  private endTimeEdited = false;

  get earliestDate(): string {
    return format(resolveFirstAvailableDate(null).date!, 'yyyy-MM-dd');
  }
  get dueDate(): string | null {
    const due = schedulingDueDay(this.data.candidate);
    return due ? format(due, 'yyyy-MM-dd') : null;
  }
  get windowIssue(): string | null {
    return resolveFirstAvailableDate(this.data.candidate).blockedReason;
  }

  @HostListener('keydown.escape')
  onEscape(): void {
    this.cancel();
  }

  /** Start changed: keep the default two-hour duration unless the user chose their own end. */
  onStartTimeChange(value: string): void {
    this.startTime = value;
    if (!this.endTimeEdited) {
      this.endTime = addMinutesToTime(value, SCHEDULE_DEFAULT_DURATION_MINUTES);
    }
  }

  onEndTimeChange(value: string): void {
    this.endTime = value;
    this.endTimeEdited = true;
  }

  cancel(): void {
    if (!this.isSaving()) {
      this.dialogRef.close(undefined);
    }
  }

  confirm(): void {
    if (this.isSaving()) {
      return;
    }

    const window = this.buildWindow();
    if (!window) {
      return;
    }

    const body: ScheduleHearingRequest = {
      ...(this.data.candidate.schedulingContext
        ? { schedulingContext: this.data.candidate.schedulingContext }
        : {}),
      scheduledStartUtc: window.startUtc,
      scheduledEndUtc: window.endUtc,
      locationName: this.locationName.trim() || null,
      roomName: this.roomName.trim() || null,
    };

    this.isSaving.set(true);
    this.scheduleService
      .scheduleHearing(this.data.candidate.scheduledProceedingId, body)
      .pipe(
        catchError((error: unknown) => {
          this.showError(error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.isSaving.set(false);
        if (result !== null) {
          this.dialogRef.close(result);
        }
      });
  }

  /**
   * Combines the date + time inputs into an ISO UTC window, surfacing inline validation instead
   * of posting an invalid request. Returns null (with the error signals set) when invalid.
   */
  private buildWindow(): { startUtc: string; endUtc: string } | null {
    const start = combineDateTime(this.date, this.startTime);
    const end = combineDateTime(this.date, this.endTime);
    if (!start || !end) {
      this.notifyError(this.hearingText.chooseDateTime());
      return null;
    }
    if (end.getTime() <= start.getTime()) {
      this.notifyError('The end time must be after the start time.');
      return null;
    }
    // The drop guard blocks past-day DROPS, but the dialog's date field is freely editable: catch
    // a typed past date here before posting. The server enforces the same rule as the backstop.
    if (isBeforeToday(start)) {
      this.notifyError(this.hearingText.pastDate());
      return null;
    }
    const issue = schedulingDateIssue(this.data.candidate, start);
    if (issue) {
      this.notifyError(issue);
      return null;
    }
    return { startUtc: start.toISOString(), endUtc: end.toISOString() };
  }

  /**
   * Surfaces a failed schedule write as the shared "Cannot schedule" EZ notification toast —
   * identical to the drag/drop calendar path and the Reschedule dialog. The specific server reason
   * (slot/room/resource conflict, after-due-date, lead time, permission, or HTTP status) rides in
   * the toast body via the shared mapper; the dialog stays open so the scheduler can pick a
   * different window.
   */
  private showError(error: unknown): void {
    this.notifyError(
      scheduleWriteErrorMessage(error, {
        permissionDenied: this.hearingText.permissionDenied(),
        conflict: this.hearingText.conflict(),
        failedTitle: this.hearingText.failedTitle(),
        failed: this.hearingText.failed(),
      }),
    );
  }

  /** Raises the shared "Cannot schedule" EZ notification with the given reason. */
  private notifyError(message: string): void {
    this.toast.showError(message, SCHEDULE_BLOCKED_TOAST_TITLE);
  }
}

/**
 * Adds `minutes` to a local "HH:mm" time, clamping at 23:59 so the default end never rolls past
 * midnight into a window the same-day date inputs cannot express. Empty/invalid input passes
 * through untouched (buildWindow reports it).
 */
function addMinutesToTime(time: string, minutes: number): string {
  const match = /^(\d{2}):(\d{2})$/.exec(time.trim());
  if (!match) {
    return time;
  }
  const total = Number(match[1]) * 60 + Number(match[2]) + minutes;
  if (total >= 24 * 60) {
    return '23:59';
  }
  const hours = String(Math.floor(total / 60)).padStart(2, '0');
  const mins = String(total % 60).padStart(2, '0');
  return `${hours}:${mins}`;
}
